import { PropertyData } from '@/services/scraper.service';
import { PropertyGroup } from './propertyGrouping';
import { MAX_CONDO_MERGE_DISTANCE_METERS } from './propertyGrouping.constants';
import { Coordinate, removeDuplicatesByUrl, calculateCentroid } from './propertyGrouping.utils';
import { calculateDistance } from './propertyGrouping';

const SAME_NAME_DISTANCE_MULTIPLIER = 2;
const AUTO_CONDO_ID_PREFIX = 'condo_auto_';

export function isCondoNameFromTitle(condo: PropertyGroup): boolean {
  return !condo.id.startsWith(AUTO_CONDO_ID_PREFIX);
}

export function getGroupCentroid(group: PropertyGroup): Coordinate | null {
  return calculateCentroid(group.properties);
}

function isWithinMergeDistance(
  point1: Coordinate,
  point2: Coordinate,
  maxDistance: number = MAX_CONDO_MERGE_DISTANCE_METERS
): boolean {
  const distance = calculateDistance(point1.lat, point1.lon, point2.lat, point2.lon);
  return distance <= maxDistance;
}

function createMergedGroup(
  baseGroup: PropertyGroup,
  groupsToMerge: PropertyGroup[],
  selectedProperties: Set<string>
): PropertyGroup {
  const allProperties = groupsToMerge.flatMap(g => g.properties);
  const uniqueProps = removeDuplicatesByUrl(allProperties);

  return {
    id: baseGroup.id,
    condominiumName: baseGroup.condominiumName,
    address: baseGroup.address,
    latitude: baseGroup.latitude,
    longitude: baseGroup.longitude,
    properties: uniqueProps,
    totalCount: uniqueProps.length,
    selectedCount: uniqueProps.filter(p => selectedProperties.has(p.url)).length,
  };
}

function mergeIntoExistingGroup(
  existingGroup: PropertyGroup,
  newGroup: PropertyGroup,
  selectedProperties: Set<string>
): void {
  const mergedProperties = [...existingGroup.properties, ...newGroup.properties];
  const uniqueProps = removeDuplicatesByUrl(mergedProperties);

  existingGroup.properties = uniqueProps;
  existingGroup.totalCount = uniqueProps.length;
  existingGroup.selectedCount = uniqueProps.filter(p => selectedProperties.has(p.url)).length;
}

function findNearbyAutoCondos(
  referenceCentroid: Coordinate,
  autoCondos: PropertyGroup[],
  alreadyMergedIds: Set<string>
): PropertyGroup[] {
  const nearby: PropertyGroup[] = [];

  for (const autoCondo of autoCondos) {
    if (alreadyMergedIds.has(autoCondo.id)) continue;

    const autoCentroid = getGroupCentroid(autoCondo);
    if (!autoCentroid) continue;

    if (isWithinMergeDistance(referenceCentroid, autoCentroid)) {
      nearby.push(autoCondo);
    }
  }

  return nearby;
}

function findNearbySameNameCondos(
  namedCondo: PropertyGroup,
  namedCentroid: Coordinate,
  allNamedCondos: PropertyGroup[]
): PropertyGroup[] {
  const nearby: PropertyGroup[] = [];
  const maxDistance = MAX_CONDO_MERGE_DISTANCE_METERS * SAME_NAME_DISTANCE_MULTIPLIER;

  for (const otherCondo of allNamedCondos) {
    if (otherCondo.id === namedCondo.id) continue;
    if (otherCondo.condominiumName !== namedCondo.condominiumName) continue;

    const otherCentroid = getGroupCentroid(otherCondo);
    if (!otherCentroid) continue;

    if (isWithinMergeDistance(namedCentroid, otherCentroid, maxDistance)) {
      nearby.push(otherCondo);
    }
  }

  return nearby;
}

function tryMergeAutoCondoWithExisting(
  autoCondo: PropertyGroup,
  existingCondos: PropertyGroup[],
  selectedProperties: Set<string>
): boolean {
  const autoCentroid = getGroupCentroid(autoCondo);
  if (!autoCentroid) return false;

  for (const existingCondo of existingCondos) {
    const existingCentroid = getGroupCentroid(existingCondo);
    if (!existingCentroid) continue;

    if (isWithinMergeDistance(autoCentroid, existingCentroid)) {
      mergeIntoExistingGroup(existingCondo, autoCondo, selectedProperties);
      return true;
    }
  }

  return false;
}

export function mergeCondominiumsByProximity(
  condominiums: PropertyGroup[],
  selectedProperties: Set<string>
): PropertyGroup[] {
  if (condominiums.length <= 1) return condominiums;

  const namedCondos = condominiums.filter(isCondoNameFromTitle);
  const autoCondos = condominiums.filter(c => !isCondoNameFromTitle(c));

  const result: PropertyGroup[] = [];
  const mergedAutoCondoIds = new Set<string>();
  const processedNamedCondoIds = new Set<string>();

  for (const namedCondo of namedCondos) {
    if (processedNamedCondoIds.has(namedCondo.id)) continue;

    const namedCentroid = getGroupCentroid(namedCondo);
    if (!namedCentroid) {
      result.push(namedCondo);
      continue;
    }

    const toMerge: PropertyGroup[] = [namedCondo];

    const nearbyAuto = findNearbyAutoCondos(namedCentroid, autoCondos, mergedAutoCondoIds);
    nearbyAuto.forEach(c => {
      toMerge.push(c);
      mergedAutoCondoIds.add(c.id);
    });

    const nearbySameName = findNearbySameNameCondos(namedCondo, namedCentroid, namedCondos);
    nearbySameName.forEach(c => {
      if (!toMerge.includes(c)) {
        toMerge.push(c);
        processedNamedCondoIds.add(c.id);
      }
    });

    if (toMerge.length > 1) {
      result.push(createMergedGroup(namedCondo, toMerge, selectedProperties));
    } else {
      result.push(namedCondo);
    }
  }

  for (const autoCondo of autoCondos) {
    if (mergedAutoCondoIds.has(autoCondo.id)) continue;

    const wasMerged = tryMergeAutoCondoWithExisting(autoCondo, result, selectedProperties);
    if (!wasMerged) {
      result.push(autoCondo);
    }
  }

  return result;
}

function findNearestCondominium(
  property: PropertyData,
  condominiums: PropertyGroup[]
): PropertyGroup | null {
  if (!property.latitude || !property.longitude) return null;

  let nearestCondo: PropertyGroup | null = null;
  let minDistance = Infinity;

  for (const condo of condominiums) {
    const condoCentroid = getGroupCentroid(condo);
    if (!condoCentroid) continue;

    const distance = calculateDistance(
      property.latitude,
      property.longitude,
      condoCentroid.lat,
      condoCentroid.lon
    );

    if (distance <= MAX_CONDO_MERGE_DISTANCE_METERS && distance < minDistance) {
      minDistance = distance;
      nearestCondo = condo;
    }
  }

  return nearestCondo;
}

function associatePropertyToCondominium(
  property: PropertyData,
  condo: PropertyGroup,
  selectedProperties: Set<string>
): void {
  if (condo.properties.some(p => p.url === property.url)) return;

  condo.properties.push(property);
  condo.totalCount++;
  if (selectedProperties.has(property.url)) {
    condo.selectedCount++;
  }
}

export function associateLoosePropertiesToCondos(
  condominiums: PropertyGroup[],
  looseProperties: PropertyData[],
  selectedProperties: Set<string>
): { finalCondominiums: PropertyGroup[]; finalLooseProperties: PropertyData[] } {
  if (condominiums.length === 0 || looseProperties.length === 0) {
    return { finalCondominiums: condominiums, finalLooseProperties: looseProperties };
  }

  const remainingLoose: PropertyData[] = [];

  for (const looseProp of looseProperties) {
    const nearestCondo = findNearestCondominium(looseProp, condominiums);

    if (nearestCondo) {
      associatePropertyToCondominium(looseProp, nearestCondo, selectedProperties);
    } else {
      remainingLoose.push(looseProp);
    }
  }

  return {
    finalCondominiums: condominiums,
    finalLooseProperties: remainingLoose,
  };
}
